import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Tooltip from '@mui/material/Tooltip';
import useToken from '../../App/useToken';
import getRole from '../../Utils/getRole';
import Table from '@mui/material/Table';
import Paper from '@mui/material/Paper';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import EnhancedTableHead from '../../Utils/EnhancedTableHead';
import { getComparator, stableSort } from '../../Utils/SortArray';
import capitalizeFirstLetter from '../../Utils/Capitalise';
import fetchWithAuth from '../../Utils/FetchWithAuth';
import useViewportWidth from '../../Utils/useViewportWidth';
import { generateHorseUrl, generateHorseSilk } from '../../Utils/RaceUtils';
import HorseLink from '../../Utils/HorseLink';
import { interactiveTableRowSx } from '../../Utils/tableRowStyles';

const smartMasterWaCellStyle = { fontWeight: 'bold', color: '#000000' };
const smartMasterTimeCellStyle = { fontWeight: 'bold', color: '#2563eb' };
const secPotentialCellStyle = { fontWeight: 'bold', color: '#dc2626' };
const speedPotentialCellStyle = { fontWeight: 'bold', color: '#2563eb' };

function getHeadCells(role, hiddenColumns = [], isFlatRacecard = false) {
  const first = 
  [
    {
      id: 'finishPosition',
      numeric: true,
      label: 'Fin Pos.',
      fixed: true,
    },
    {
      id: 'raceCard',
      numeric: false,
      label: 'Race Card',
      fixed: true, 
      align: 'left',
      sticky: true
    },
    {
      id: 'horseName',
      numeric: false,
      label: 'Horse Name',
      fixed: true,
      align: 'left',
    },
    {
      id: 'ownerName',
      numeric: false,
      label: 'Owner',
      align: 'left',
    },
    {
      id: 'trainerName',
      numeric: false,
      label: 'Trainer',
      align: 'left',
    },
    {
      id: 'OSEX',
      numeric: false,
      label: 'Sex',
      align: 'right',
    },
    {
      id: 'OAGE',
      numeric: true,
      label: 'Age',
    },
    // {
    //   id: 'OSADDLE',
    //   numeric: true,
    //   label: 'Saddle',
    // },
    // {
    //   id: 'ODRAW',
    //   numeric: true,
    //   label: 'Draw',
    // },
    {
      id: 'OWEIGHT',
      numeric: true,
      label: 'Weight',
    },
    
  ]
  const second = [
    {
      id: isFlatRacecard ? 'RacecardNotable' : 'OALLOW',
      numeric: !isFlatRacecard,
      label: isFlatRacecard ? 'Notable' : 'Allowance',
      align: isFlatRacecard ? 'left' : 'right',
    },
    {
      id: 'OR',
      numeric: true,
      label: 'ORpre',
    }
  ]
  const third = [
    {
      id: 'ORpost',
      numeric: true,
      label: 'OR Post',
    }
  ]
  const fourth = [
    {
      id: 'RPRpost',
      numeric: true,
      label: 'RPR Post',
    },
    {
      id: 'TFpost',
      numeric: true,
      label: 'TF Post',
    },
    {
      id: 'TFtfigPost',
      numeric: true,
      label: 'TF Tfig Post',
    },
    
  ]
  const fifth = [
    {
      id: 'SmartMasterWgtAdj',
      numeric: true,
      label: 'Smart Master WA',
    }
  ]
  const sixth = [
    {
      id: 'SmartMasterTimePre',
      numeric: true,
      label: 'Smart Master Time Pre',
    }
  ]
  const sixthExtra = [
    {
      id: 'SecPotential',
      numeric: true,
      label: 'Sec Potential',
    },
    {
      id: 'SpeedPotential',
      numeric: true,
      label: 'Speed Potential',
    }
  ]
  const seventh = [
    {
      id: 'SmartForm',
      numeric: true,
      label: 'Smart Form',
    },
    {
      id: 'SmartTime',
      numeric: true,
      label: 'Smart Time',
    },
  ]
  const eighth = [
    {
      id: 'SmartSecTime',
      numeric: true,
      label: 'Smart Sec Time',
    }
  ]

  const ninth = [
    {
      id: 'SmartRating',
      numeric: true,
      label: 'Smart Rating',
    }
  ]

  const tenth = [
    {
      id: 'Source',
      numeric: false,
      label: 'SecSource ',
      fixed: false,
      align: 'right',
    },
    {
      id: 'RankRnrs',
      numeric: true,
      label: 'Rank Rnrs',
      fixed: false,
    },
    {
      id: 'timeRnk',
      numeric: true,
      label: 'Time Rank Pct',
      fixed: false,
    },
    {
      id: 'secRnk',
      numeric: true,
      label: 'Sec Rank Pct',
      fixed: false,
    },
    {
      id: 'cadenceMax',
      numeric: true,
      label: 'Cadence Max',
      fixed: false,
    },
    {
      id: 'cadenceMaxRnk',
      numeric: true,
      label: 'Cadence Max Rank',
      fixed: false,
    },
    {
      id: 'stdLthMax',
      numeric: true,
      label: 'Std Lth Max (m)',
      fixed: false,
    },
    {
      id: 'stdLthRnk',
      numeric: true,
      label: 'Std Lth Rank',
      fixed: false,
    },
  ]

  // Filter out the columns that should be hidden
  const filteredThird = third.filter(col => !hiddenColumns.includes(col.id));
  const filteredFourth = fourth.filter(col => !hiddenColumns.includes(col.id));
  const filteredTenth = tenth.filter(col => !hiddenColumns.includes(col.id));

  if (role === "admin" || role === "friend") {
    return first.concat(second, fifth, sixth, sixthExtra, seventh, eighth, ninth, filteredThird, filteredFourth, filteredTenth);
  } 
  
  if (role === "blandford" || role === "coolmore" || role === "framont") {
    return first.concat(second, fifth, sixth, sixthExtra, seventh, eighth, ninth, filteredThird, filteredFourth, filteredTenth);
  }

  // For trial role, use a restricted set of columns
  return first.concat(second, fifth, seventh, ninth);
}

function isMissingRacecardValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value !== 'string') return false;

  const normalizedValue = value.trim().toLowerCase();
  return normalizedValue === '' || normalizedValue === 'nan' || normalizedValue === 'na';
}

function getRacecardNumber(value) {
  if (isMissingRacecardValue(value)) return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function formatRacecardDecimal(value) {
  const numberValue = getRacecardNumber(value);
  return numberValue === null ? '' : numberValue.toFixed(1);
}

function getSpeedPotential(horse) {
  const smartMasterTimePre = getRacecardNumber(horse?.SmartMasterTimePre);
  const secPotential = getRacecardNumber(horse?.SecPotential);
  if (smartMasterTimePre === null) return null;
  return secPotential === null ? smartMasterTimePre : smartMasterTimePre + secPotential;
}

function addRacecardCalculatedValues(horse) {
  if (!horse) return horse;
  return {
    ...horse,
    SpeedPotential: getSpeedPotential(horse),
  };
}

function formatNotableText(value) {
  if (isMissingRacecardValue(value)) return '';
  return String(value)
    .replace(/<br\s*\/?>/gi, ' | ')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function CountryRacecard ({ text, hiddenColumns = [] }) {
  // //API CALL
  const [error, setError] = useState(null);
  const [raceLoaded, setRaceLoaded] = useState(false);
  const [possDates, setPossDates] = useState([]);
  const [possCourses, setPossCourses] = useState([]);
  const [possTimes, setPossTimes] = useState([]);
  const [horses, setHorses] = useState([]);
  const [raceInfo, setRaceInfo] = useState([]);
  const [smartContent, setSmartContent] = useState([]);
  const [meetingRaces, setMeetingRaces] = useState([]);
  const [raceDetails, setRaceDetails] = useState({});
  const [raceDate, setRaceDate] = useState(new Date().toISOString().split('T')[0]);
  const [raceDataSource, setRaceDataSource] = useState('');
  const [course, setCourse] = useState(null);
  const [time, setTime] = useState(null);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('SmartMasterWgtAdj');
  const [sortedRows, setSortedRows] = useState([]);
  const [allTablesSortState, setAllTablesSortState] = useState({});

  const { token } = useToken();
  const role = getRole();
  const screenWidth = useViewportWidth();
  const isMobileView = screenWidth < 1000;
  const country = text.countryCode.split('_')[0];
  const isFlatRacecard = text.countryCode.endsWith('_F');
  const hasExtendedRacecardAccess = role === 'admin' || role === 'blandford' || role === 'coolmore' || role === 'framont' || role === 'friend';
  
  // Add columns to hide for framont role
  const finalHiddenColumns = (role === 'framont') 
    ? [...hiddenColumns, 'TFpost', 'TFtfigPost', 'Source', 'RankRnrs', 'timeRnk', 'secRnk', 'cadenceMax', 'cadenceMaxRnk', 'stdLthMax', 'stdLthRnk']
    : hiddenColumns;

  const loadRaces = (e) => {
    e.preventDefault()
  }

  // Reset race data when time changes
  useEffect(() => {
    if (time) {
      setHorses([]);
      setRaceInfo([]);
      setSmartContent([]);
      setRaceLoaded(false);
    }
  }, [time]);

  // Reset course and time when date changes
  useEffect(() => {
    setCourse(null);
    setTime(null);
  }, [raceDate]);

  // Reset time when course changes
  useEffect(() => {
    setTime(null);
  }, [course]);

  // Reset meeting races when date changes
  useEffect(() => {
    setMeetingRaces([]);
    setCourse(null);
    setTime(null);
  }, [raceDate]);
  
  // Reset meeting races when course changes
  useEffect(() => {
    setMeetingRaces([]);
    setTime(null);
  }, [course]);

  const handleRequestSort = (
    event,
    property,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleAllTablesRequestSort = (raceIdentifier, property) => {
    const isAsc = allTablesSortState[raceIdentifier]?.orderBy === property && allTablesSortState[raceIdentifier]?.order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
  
    setAllTablesSortState({
      ...allTablesSortState,
      [raceIdentifier]: {
        order: newOrder,
        orderBy: property,
      },
    });
  };
  

  useEffect(() => {
    fetchWithAuth('dates', {countryCode: text.countryCode}, token)
      .then((result) => setPossDates(result.reverse()))
      .catch((error) => setError(error))
  }, [])

  useEffect(() => {
    if (raceDate) {
      fetchWithAuth('courses', {countryCode: text.countryCode, raceDate: raceDate}, token)
        .then((result) => setPossCourses(result))
        .catch((error) => setError(error));
    } else {
      setPossCourses([]);
    }
  }, [raceDate, text.countryCode, token]);

  useEffect(() => {
    if (course && raceDate) {
      fetchWithAuth('racesByTime', {countryCode: text.countryCode, course: course, raceDate: raceDate}, token)
        .then((result) => setPossTimes(result))
        .catch((error) => setError(error));
    } else {
      setPossTimes([]);
    }
  }, [course, raceDate, text.countryCode, token]);
  
  // For All races option
  useEffect(() => {
    if (course && raceDate && time === 'All') {
      setMeetingRaces([]); 
      
      // Instead of constructing a smartMeetID directly, let's fetch all races for the specific date and course
      // This is a simpler, more robust approach that avoids issues with smartMeetID formats
      fetchWithAuth('racesForMeeting', { course: course, raceDate: raceDate, countryCode: text.countryCode }, token)
        .then(async (meetingRaces) => {
          if (!Array.isArray(meetingRaces) || meetingRaces.length === 0) {
            throw new Error('No valid meeting data found');
          }
  
          const detailedRacesPromises = meetingRaces.map(async (race) => {
            try {
              // Use the original race time for API calls to ensure consistent behavior
              const raceTime = race.raceTime;
              
              const [raceDetails, horseDetails] = await Promise.all([
                fetchWithAuth('raceLevelInfo', {
                  countryCode: text.countryCode,
                  course: course,
                  raceTime: raceTime,
                  raceDate: raceDate
                }, token),
                fetchWithAuth('horseLevelInfo', {
                  countryCode: text.countryCode,
                  course: course,
                  raceTime: raceTime,
                  raceDate: raceDate
                }, token)
              ]);
  
              // Ensure we have valid data
              if (!raceDetails?.[0] || !horseDetails?.[0]?.horses) {
                console.warn(`Incomplete data for race at ${raceTime}`);
                return null;
              }
  
              let initialSorting = {
                order: 'desc',
                orderBy: 'SmartMasterWgtAdj'
              };
  
              if (raceDetails[0]?.PostRaceRated === 1) {
                initialSorting = {
                  order: 'asc',
                  orderBy: 'finishPosition'
                };
              }
  
              return {
                ...race,
                raceDetails: raceDetails[0],
                horseDetails: horseDetails[0].horses,
                raceDate: raceDate,
                course: course,
                country: country,
                initialSorting
              };
            } catch (error) {
              console.warn(`Error fetching details for race at ${race.raceTime}:`, error);
              return null;
            }
          });
  
          const detailedRaces = await Promise.all(detailedRacesPromises);
          
          // Filter out null results and only update if still on "All" view
          if (time === 'All') {
            const validRaces = detailedRaces.filter(race => race !== null);
            if (validRaces.length === 0) {
              throw new Error('No valid race data found');
            }
            setMeetingRaces(validRaces);
          }
        })
        .catch((error) => {
          console.error('Error fetching meeting data:', error);
          setError(error);
          setMeetingRaces([]); // Clear meeting races on error
          // Don't reset time selection on error to allow for retries
        });
    }
  }, [course, raceDate, time, text.countryCode, token]);

  // For individual races
  useEffect(() => {
    if (time) {
      // Fetching horse level info
      fetchWithAuth('horseLevelInfo', {countryCode: text.countryCode, course: course, raceTime: time, raceDate: raceDate}, token)
        .then((result) => {
          // Console logging for each horse's silkCode and horseID
          result[0].horses.forEach(horse => {
            // console.log('Horse Level Info:', {
            //   silkCode: horse.silkCode,
            //   horseID: horse.horseID
            // });
          });
          setHorses(result[0].horses);
        })
        .catch((error) => setError(error));
  
      // Fetching race level info
      fetchWithAuth('raceLevelInfo', {countryCode: text.countryCode, course: course, raceTime: time, raceDate: raceDate}, token)
        .then((result) => {
          const raceInfo = result[0];
          // Console logging for the dataSource for the race
          // console.log('Race Level Info:', {
          //   smartRaceID: raceInfo.smartRaceID,
          //   dataSource: raceInfo.dataSource // Assuming dataSource is now being fetched correctly
          // });

          setRaceDataSource(raceInfo.dataSource);
  
          setRaceInfo([
            { id: 1, name: 'Smart Race ID', value: raceInfo.smartRaceID },
            { id: 2, name: 'Official Going', value: raceInfo.OGOING },
            { id: 3, name: 'Distance (f)', value: `${isNaN(Number(raceInfo.distExact).toFixed(1)) ? '' : Number(raceInfo.distExact).toFixed(1)}` },
            { id: 4, name: 'Expected Strength', value: `${isNaN(Number(raceInfo.XStrength).toFixed(1)) ? '' : Number(raceInfo.XStrength).toFixed(1)}` },
            { id: 5, name: 'Race Title', value: raceInfo.raceTitle },
            { id: 6, name: 'Age', value: raceInfo.ageConditions },
            { id: 7, name: 'Gender', value: raceInfo.genderConditions },
            { id: 8, name: 'Handicap Level', value: raceInfo.hcapConditions },
            // { id: 6, name: 'Data Source', value: raceInfo.dataSource || 'N/A' }
          ]);
  
          // Existing logic to determine orderBy and order
          if (raceInfo.PostRaceRated === 1) { 
            setOrderBy('finishPosition');
            setOrder('asc');
          } else {
            setOrderBy('SmartMasterWgtAdj');
          }
        })
        .catch((error) => setError(error))
        .finally(() => setRaceLoaded(true));
    }
  }, [time, course, text.countryCode, raceDate, token]); 

  // FIXED: Don't remove SmartForm and SmartRating just because SmartTime is missing
  // Only remove data for DNF horses, not for pre-race rated horses that don't have time data yet
  useEffect(() => {
    if (horses && horses.length > 0) {
      const processedHorses = horses.map((horse) => {
        // Check if it's a DNF horse (will have SmartFormExists=1 but SmartTimeExists=0)
        // or if it's a pre-race rated horse (will have SmartFormExists=1 regardless of SmartTimeExists)
        const isDNF = horse.finishPosition === 'DNF' || 
                      horse.finishPosition === 'PU' || 
                      horse.finishPosition === 'UR';
        
        // Only remove SmartForm and SmartRating for actual DNF horses,
        // not for horses that just don't have time data yet
        if (isDNF && (horse.SmartTime === null || horse.SmartTime === 'NaN' || horse.SmartTime === undefined)) {
          const { SmartRating, SmartForm, ...remainingHorseData } = horse; 
          return remainingHorseData; 
        }
        return horse; 
      });
      setHorses(processedHorses); 
    }
  }, [horses]);
  

  useEffect(() => {
    let isMounted = true;  
  
    if (raceLoaded && raceInfo[0]?.value) {
      // Clear existing content before fetching new content
      setSmartContent([]);  
  
      fetchWithAuth('SmartRaceContent', {
        collection: 'SmartOutings', 
        smartRaceID: raceInfo[0].value
      }, token)
        .then((result) => {
          if (isMounted) {  // Only set state if component is still mounted
            setSmartContent(result);
          }
        })
        .catch((error) => {
          if (isMounted) {
            setError(error);
          }
        });
    }
  
    // Cleanup function
    return () => {
      isMounted = false;  
    };
  }, [raceLoaded, raceInfo, token]);  
  
  useEffect(() => {
    if (horses) {
      const rowsWithCalculatedValues = horses.map(addRacecardCalculatedValues);
      const [emptyValuesHorses, nonEmptyValuesHorses] = rowsWithCalculatedValues.reduce(([empty, nonEmpty], horse) => {
        const isEmpty = !horse[orderBy] && horse[orderBy] !== 0; // Adjust based on what constitutes 'empty'
        return isEmpty ? [[...empty, horse], nonEmpty] : [empty, [...nonEmpty, horse]];
      }, [[], []]);

      const sortedNonEmptyHorses = stableSort(nonEmptyValuesHorses, getComparator(order, orderBy));

      const finalSortedHorses = [...sortedNonEmptyHorses, ...emptyValuesHorses];
      setSortedRows(finalSortedHorses);
    }
  }, [order, orderBy, horses]);

  useEffect(() => {
    setAllTablesSortState({});
  }, [raceDate, course]);

  return (
        <div>
          <div className="mx-auto w-56 text-center">
            <form onChange={loadRaces} className="mt-6">
              <label htmlFor="racedate" className="block text-sm font-medium leading-6 text-gray-900">Select Date:</label>
              <DatePicker
                selected={new Date(raceDate)}
                onChange={(date) => {
                  setRaceDate(date.toISOString().split('T')[0]); // Update the race date
                  setTime(""); // Reset the time to the initial state (forcing "Select Race Time")
                  // Optionally reset other states or perform additional actions as needed
                }} 
                dateFormat="dd-MMM-yyyy"
                className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />

              <label htmlFor="course" className='mt-4 block text-sm font-medium leading-6 text-gray-900'>Choose a Course:</label>
              <select
                id="course"
                name="course"
                className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
                value={course || ''} // Add explicit value binding
                onChange={(e) => {
                  const newCourse = e.target.value;
                  setCourse(newCourse);
                  setTime(''); // Reset time selection, including 'All'
                  setMeetingRaces([]); // Clear any existing meeting races
                  setAllTablesSortState({}); // Reset sorting state
                }}
              >
                <option value="">Select Course</option>
                {possCourses.map((course) => (
                  <option key={course} value={course}>
                    {course}
                  </option>
                ))}
              </select>
              <label htmlFor="time" className='mt-4 block text-sm font-medium leading-6 text-gray-900'>Choose a Race Time:</label>
              <select
                id="time"
                name="time"
                value={time} 
                onChange={(e) => {
                  const newTime = e.target.value;
                  setTime(newTime);
                  // Clear meeting races when changing time selection
                  if (newTime !== 'All') {
                    setMeetingRaces([]);
                  }
                }}
                className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
              >
                <option value="">Select Race Time</option> 
                {possTimes.map((timeOption) => (
                  <option key={timeOption} value={timeOption}>
                    {timeOption}
                  </option>
                ))}
                {course && <option value="All">All</option>}
              </select>

            </form>
          </div>
        {time !== 'All' && (
          <div className="bg-white py-12 sm:py-12">
            <div className="mx-auto px-6 lg:max-w-none">
              <div className="text-center">
                <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                  Smart Content 
                </h2>
              </div>
              <ul role="list" className="divide-y divide-gray-100">
                {smartContent.map((comment) => (
                  <li key={comment.id} className="flex gap-x-4 py-5">
                    <div className="flex-auto">
                      <div className="flex items-baseline justify-between gap-x-4">
                        <p className="text-sm font-semibold leading-6 text-gray-900">{`${comment.preOrPost} - ${capitalizeFirstLetter(comment.contentLevel)} Level`}</p>
                        <p className="flex-none text-xs text-gray-600">
                          <p>{comment.contentType}</p>
                        </p>
                      </div>
                      <p className="mt-1 text-sm leading-6 text-gray-600">{comment.content}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      {time !== 'All' && (
        <div className="bg-white py-12 sm:py-12">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl lg:max-w-none">
              <div className="text-center">
                <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                  Race Information
                </h2>
                <p className="mt-4 text-lg leading-8 text-gray-600">
                  Information available at the horse level.
                </p>
              </div>
              <dl class="mt-16 grid grid-cols-1 gap-0.5 overflow-hidden rounded-2xl text-center sm:grid-cols-2 lg:grid-cols-4">
              {raceInfo.map((val, index) => {
                // Check the 'Handicaps' value only once before mapping begins
                const handicapsValue = raceInfo.find(info => info.id === 8)?.value;
                const isHandicapsNull = handicapsValue === null;

                // Condition to skip rendering if the id is 8 and value is null
                if (val.id === 8 && isHandicapsNull) {
                  return null;
                }

                // Determine column span
                let colSpan = '';
                if (index === 4) {
                  colSpan = 'lg:col-span-4'; // Race title spans all columns
                } else if (isHandicapsNull && (val.id === 6 || val.id === 7)) {
                  colSpan = 'lg:col-span-2'; // Double span for id 6 and 7 if Handicaps is null
                } else if (!isHandicapsNull && (val.id === 6 || val.id === 7 || val.id === 8)) {
                  colSpan = 'lg:col-span-1'; // Normal single span when Handicaps is not null
                }

                return (
                  <div key={val.id} className={`flex flex-col bg-gray-400/5 p-8 ${colSpan}`}>
                    <dt className="text-sm font-semibold leading-6 text-gray-600">{val.name}</dt>
                    <dd className="order-first text-2xl font-semibold tracking-tight text-gray-900">{val.value}</dd>
                  </div>
                );
              })}
              </dl>
            </div>
          </div>
        </div>
      )}
      {time !== 'All' && (
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Race Card</h2>
          <p className="mt-4 text-lg leading-8 text-gray-600">
            WA - Weight Adjusted <br />
            OR - Official Rating <br />
          </p>
        </div>
      )}  

        {time !== 'All' ? ( 
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer 
                sx=
                {{ 
                  // maxHeight: 600 
                }}
            >
              <Table
                stickyHeader
                aria-label="sticky table"
              >
                <EnhancedTableHead
                  headCells={getHeadCells(role, finalHiddenColumns, isFlatRacecard)}
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {sortedRows.map((horse) => {                    
                  return(
                    <TableRow 
                      hover
                      key={horse.id}
                      sx={interactiveTableRowSx}
                    >
                      <TableCell 
                        align='right'
                      >
                        {horse.finishPosition}
                      </TableCell>
                      <TableCell 
                        sx={{
                          ...(isMobileView ? {
                            left: 0,
                            wordWrap: "break-word",
                            fontWeight: "bold",
                          } : { 
                            position: "sticky",
                            left: 0,
                            zIndex: 800,
                            wordWrap: "break-word",
                            fontWeight: "bold",
                          })
                        }}
                      >
                        <div className="mb-2 mr-2" style={{ fontWeight: 'bold' }}>
                          {typeof horse.ODRAW !== 'undefined' && horse.ODRAW !== null ? `${horse.OSADDLE} (${horse.ODRAW})` : `${horse.OSADDLE}`}
                        </div>

                        <div>
                          <img 
                            src={generateHorseSilk(raceDataSource, horse.silkCode, raceDate, course, time, horse.OSADDLE, country)}
                            alt=""
                            style={{ width: '50px', height: 'auto' }}
                          />
                        </div>
                      </TableCell>
                      <TableCell 
                        sx={{
                          ...(isMobileView ? {
                            left: 0,
                            wordWrap: "break-word",
                            fontWeight: "bold",
                          } : { 
                            "position": "sticky",
                            "left": "92px",
                            "zIndex": 800,
                            "wordWrap": "break-word", 
                            "fontWeight": "bold"
                          })
                        }}
                      >
                        <HorseLink 
                          horse={{
                            ...horse,
                            dataSource: raceDataSource,
                            raceDate: raceDate,
                            course: course,
                            raceTime: time,
                            country: country
                          }}
                        />
                      </TableCell>
                      <TableCell align='left'>
                        <Tooltip title={horse.ownerName}>
                          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '75px' }}>
                          {horse.ownerName === 'NaN' || horse.ownerName === 'NA' || horse.ownerName == null ? '' : horse.ownerName}
                          </div>
                        </Tooltip>
                        </TableCell>
                        <TableCell align='left'>
                        <Tooltip title={horse.trainerName}>
                          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '75px' }}>
                          {horse.trainerName}
                          </div>
                        </Tooltip>
                      </TableCell>
                      <TableCell 
                      align='right'
                      >
                        {horse.OSEX}
                      </TableCell>
                      <TableCell 
                      align='right'
                      >
                        {horse.OAGE}
                      </TableCell>
                      {/* <TableCell 
                      align='right'
                      >
                        {horse.OSADDLE}
                      </TableCell> */}
                      {/* {!shouldHideDrawColumn && (
                        <TableCell align="right">
                          {horse.ODRAW}
                        </TableCell>
                      )} */}
                      <TableCell 
                      align='right'
                      >
                        {horse.OWEIGHT}
                      </TableCell>
                      <TableCell align={isFlatRacecard ? 'left' : 'right'}>
                        {isFlatRacecard ? (
                          <Tooltip title={formatNotableText(horse.RacecardNotable)}>
                            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }}>
                              {formatNotableText(horse.RacecardNotable)}
                            </div>
                          </Tooltip>
                        ) : horse.OALLOW}
                      </TableCell>
                      <TableCell 
                      align='right'
                      >
                        {horse.OR === 'NaN' || horse.OR === 'NA' || horse.OR == null ? '' : horse.OR}
                      </TableCell>
                      <TableCell 
                      align='right'
                      style={smartMasterWaCellStyle}
                      >
                        {formatRacecardDecimal(horse.SmartMasterWgtAdj)}
                      </TableCell>
                      {hasExtendedRacecardAccess?
                      <TableCell 
                      align='right'
                      style={smartMasterTimeCellStyle}
                      >
                        {formatRacecardDecimal(horse.SmartMasterTimePre)}
                      </TableCell> 
                      : null }
                      {hasExtendedRacecardAccess?
                      <TableCell
                      align='right'
                      style={secPotentialCellStyle}
                      >
                        {formatRacecardDecimal(horse.SecPotential)}
                      </TableCell>
                      : null }
                      {hasExtendedRacecardAccess?
                      <TableCell
                      align='right'
                      style={speedPotentialCellStyle}
                      >
                        {formatRacecardDecimal(horse.SpeedPotential)}
                      </TableCell>
                      : null }
                      <TableCell 
                      align='right'
                      style={{ fontWeight: 'bold' }}
                      >
                        {(horse.SmartForm === 'NaN' || horse.SmartForm == null || !horse.SmartFormExists) ? '' : Number(horse.SmartForm).toFixed(1)}
                      </TableCell>
                      <TableCell 
                      align='right'
                      style={{ fontWeight: 'bold' }}
                      >
                        {(horse.SmartTime === 'NaN' || horse.SmartTime == null || !horse.SmartTimeExists) ? '' : Number(horse.SmartTime).toFixed(1)}
                      </TableCell>
                      {hasExtendedRacecardAccess?
                      <TableCell 
                      align='right'
                      style={{ fontWeight: 'bold' }}
                      >
                        {(horse.SmartSecTime === 'NaN' || horse.SmartSecTime == null || !horse.SmartSecTimeExists) ? '' : Number(horse.SmartSecTime).toFixed(1)}
                      </TableCell>
                      : null }
                      <TableCell 
                      align='right'
                      style={{ fontWeight: 'bold' }}
                      >
                        {(horse.SmartRating === 'NaN' || horse.SmartRating == null || !horse.SmartRatingExists) ? '' : Number(horse.SmartRating).toFixed(1)}
                      </TableCell>
                      {role === 'admin' || role === 'friend' || role === 'blandford' || role === 'coolmore'? (
                        !hiddenColumns.includes('ORpost') ? (
                          <TableCell 
                            align='right'
                            style={{ fontWeight: 'bold' }}
                          >
                            {horse.ORpost === 'NaN' || horse.ORpost === 'NA' || horse.ORpost == null ? '' : Math.round(Number(horse.ORpost))}
                          </TableCell>
                        ) : null
                      ) : null}
                      
                      {role === 'admin' || role === 'friend' || role === 'blandford' || role === 'coolmore' ? (
                        !hiddenColumns.includes('RPRpost') ? (
                          <TableCell 
                            align='right'
                            style={{ fontWeight: 'bold' }}
                          >
                            {horse.RPRpost === 'NaN' || horse.RPRpost === 'NA' || horse.RPRpost == null ? '' : Math.round(Number(horse.RPRpost))}
                          </TableCell>
                        ) : null
                      ) : null}
                      
                      {role === 'admin' || role === 'friend' || role === 'blandford'  || role === 'coolmore' ? (
                        !finalHiddenColumns.includes('TFpost') ? (
                          <TableCell 
                            align='right'
                            style={{ fontWeight: 'bold' }}
                          >
                            {horse.TFpost === 'NaN' || horse.TFpost === 'NA' || horse.TFpost == null || horse.TFpost === 0 || horse.TFpost === '0' || Number(horse.TFpost) === 0 ? '' : Math.round(Number(horse.TFpost))}
                          </TableCell>
                        ) : null
                      ) : null}

                      
                      {role === 'admin' || role === 'friend' || role === 'blandford'  || role === 'coolmore' ? (
                        !finalHiddenColumns.includes('TFtfigPost') ? (
                          <TableCell 
                            align='right'
                            style={{ fontWeight: 'bold' }}
                          >
                            {horse.TFtfigPost === 'NaN' || horse.TFtfigPost === 'NA' || horse.TFtfigPost == null || horse.TFtfigPost === 0 || horse.TFtfigPost === '0' || Number(horse.TFtfigPost) === 0 ? '' : Math.round(Number(horse.TFtfigPost))}
                          </TableCell>
                        ) : null
                      ) : null}

                     {/* Third party adder variables - only include for specific roles */}
                      {(role === 'admin' || role === 'friend' || role === 'blandford' || role === 'coolmore' || role === 'framont') && (
                        <React.Fragment>
                          {!finalHiddenColumns.includes('Source') && (
                            <TableCell
                              align='right'
                              style={{ fontWeight: 'bold' }}
                            >
                              {horse.Source !== 'NaN' && horse.Source}
                            </TableCell>
                          )}
                          
                          {!finalHiddenColumns.includes('RankRnrs') && (
                            <TableCell
                              align='right'
                              style={{ fontWeight: 'bold' }}
                            >
                              {horse.RankRnrs === 'NaN' || horse.RankRnrs == null ? '' : Math.round(Number(horse.RankRnrs))}
                            </TableCell>
                          )}
                          
                          {!finalHiddenColumns.includes('timeRnk') && (
                            <TableCell
                              align='right'
                              style={{ fontWeight: 'bold' }}
                            >
                              {horse.timeRnk === 'NaN' || horse.timeRnk == null ? '' : Number(horse.timeRnk).toFixed(1)}
                            </TableCell>
                          )}
                          
                          {!finalHiddenColumns.includes('secRnk') && (
                            <TableCell
                              align='right'
                              style={{ fontWeight: 'bold' }}
                            >
                              {horse.secRnk === 'NaN' || horse.secRnk == null ? '' : Number(horse.secRnk).toFixed(1)}
                            </TableCell>
                          )}
                          
                          {!finalHiddenColumns.includes('cadenceMax') && (
                            <TableCell
                              align='right'
                              style={{ fontWeight: 'bold' }}
                            >
                              {horse.cadenceMax === 'NaN' || horse.cadenceMax == null ? '' : Number(horse.cadenceMax).toFixed(2)}
                            </TableCell>
                          )}
                          
                          {!finalHiddenColumns.includes('cadenceMaxRnk') && (
                            <TableCell
                              align='right'
                              style={{ fontWeight: 'bold' }}
                            >
                              {horse.cadenceMaxRnk === 'NaN' || horse.cadenceMaxRnk == null ? '' : Number(horse.cadenceMaxRnk).toFixed(1)}
                            </TableCell>
                          )}
                          
                          {!finalHiddenColumns.includes('stdLthMax') && (
                            <TableCell
                              align='right'
                              style={{ fontWeight: 'bold' }}
                            >
                              {horse.stdLthMax === 'NaN' || horse.stdLthMax == null ? '' : Number(horse.stdLthMax).toFixed(2)}
                            </TableCell>
                          )}
                          
                          {!finalHiddenColumns.includes('stdLthRnk') && (
                            <TableCell
                              align='right'
                              style={{ fontWeight: 'bold' }}
                            >
                              {horse.stdLthRnk === 'NaN' || horse.stdLthRnk == null ? '' : Number(horse.stdLthRnk).toFixed(1)}
                            </TableCell>
                          )}
                        </React.Fragment>
                      )}
                        
                  </TableRow>
                  )
                })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <div>
            {meetingRaces.map((race, index) => {
              if (!race || !race.raceDetails) {
                return null; // Skip rendering if we don't have valid race details
              }
            
              const raceIdentifier = race.raceTime;
              const raceDataSource = race.raceDetails?.dataSource || '';
              const ageConditions = race.raceDetails?.ageConditions || 'N/A';
              const genderConditions = race.raceDetails?.genderConditions || 'N/A';
              const hcapConditions = race.raceDetails?.hcapConditions || 'N/A';
              const currentSortState = allTablesSortState[raceIdentifier] || race.initialSorting || {
                order: 'asc',
                orderBy: 'SmartMasterWgtAdj',
              };
            
              // Extract race-specific variables with fallbacks
              const raceDate = race.raceDate || 'defaultDate';
              const course = race.course || 'defaultCourse';
              const time = race.raceTime || 'defaultTime';
              const country = race.country || 'GB';
              
              // Ensure horseDetails exists before processing
              const horseDetails = (race.horseDetails || []).map(addRacecardCalculatedValues);
              
              // Add null checks to the sorting logic
              const [emptyValuesHorses, nonEmptyValuesHorses] = horseDetails.reduce(([empty, nonEmpty], horse) => {
                if (!horse) return [empty, nonEmpty];
                const value = horse[currentSortState.orderBy];
                const isEmpty = value === '' || value === null || value === undefined;
                return isEmpty ? [[...empty, horse], nonEmpty] : [empty, [...nonEmpty, horse]];
              }, [[], []]);
            
              const comparator = getComparator(currentSortState.order, currentSortState.orderBy);
              const sortedNonEmptyHorses = stableSort(nonEmptyValuesHorses, comparator);
              const finalSortedHorses = [...sortedNonEmptyHorses, ...emptyValuesHorses];
            
              // Only render if we have valid data
              if (!race.distExact || !race.OGOING || !race.raceTitle) {
                return null;
              }

              return (
                <div key={index}>
                <h3 className="text-lg font-semibold py-2 px-4 bg-gray-200 mt-20">{course} - {race.raceTime} - {race.distExact.toFixed(1)}f - {race.OGOING} - {race.raceTitle} - {ageConditions} - {genderConditions} - {hcapConditions}</h3> 
                <Paper sx={{ width: '100%', overflow: 'hidden', marginBottom: '16px' }}>
                  <TableContainer 
                    sx=
                      {{ 
                        // maxHeight: 600 
                      }}
                    >
                    <Table stickyHeader aria-label="sticky table">
                      <EnhancedTableHead
                        headCells={getHeadCells(role, finalHiddenColumns, isFlatRacecard)}
                        order={currentSortState.order}
                        orderBy={currentSortState.orderBy}
                        onRequestSort={(event, property) => handleAllTablesRequestSort(raceIdentifier, property)}
                      />
                      <TableBody>
                        {finalSortedHorses.map((horse) => (   
                          <TableRow 
                          hover
                          key={horse.id}
                          sx={interactiveTableRowSx}
                          >
                            <TableCell 
                            align='right'
                            >
                              {horse.finishPosition}
                            </TableCell>
                            <TableCell 
                              sx={{
                                ...(isMobileView ? {
                                  left: 0,
                                  wordWrap: "break-word",
                                  fontWeight: "bold",
                                } : { 
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 800,
                                  wordWrap: "break-word",
                                  fontWeight: "bold",
                                })
                              }}
                            >
                              <div className="mb-2 mr-2" style={{ fontWeight: 'bold' }}>
                                {typeof horse.ODRAW !== 'undefined' && horse.ODRAW !== null ? `${horse.OSADDLE} (${horse.ODRAW})` : `${horse.OSADDLE}`}
                              </div>

                              <div>
                                <img 
                                  src={generateHorseSilk(raceDataSource, horse.silkCode, raceDate, course, time, horse.OSADDLE, country)}
                                  alt=""
                                  style={{ width: '50px', height: 'auto' }}
                                />
                              </div>
                            </TableCell>
                            <TableCell 
                              sx={{
                                ...(isMobileView ? {
                                  left: 0,
                                  wordWrap: "break-word",
                                  fontWeight: "bold",
                                } : { 
                                  "position": "sticky",
                                  "left": "92px",
                                  "zIndex": 800,
                                  "wordWrap": "break-word", 
                                  "fontWeight": "bold"
                                })
                              }}
                            >
                              <HorseLink 
                                horse={{
                                  ...horse,
                                  dataSource: raceDataSource,
                                  raceDate: raceDate,
                                  course: course,
                                  raceTime: time,
                                  country: country
                                }}
                              />
                            </TableCell>
                            <TableCell align='left'>
                              <Tooltip title={horse.ownerName}>
                                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '75px' }}>
                                {horse.ownerName === 'NaN' || horse.ownerName === 'NA' || horse.ownerName == null ? '' : horse.ownerName}
                                </div>
                              </Tooltip>
                              </TableCell>
                              <TableCell align='left'>
                              <Tooltip title={horse.trainerName}>
                                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '75px' }}>
                                {horse.trainerName}
                                </div>
                              </Tooltip>
                            </TableCell>
                            <TableCell 
                            align='right'
                            >
                              {horse.OSEX}
                            </TableCell>
                            <TableCell 
                            align='right'
                            >
                              {horse.OAGE}
                            </TableCell>
                            {/* <TableCell 
                            align='right'
                            >
                              {horse.OSADDLE}
                            </TableCell>
                            {!shouldHideDrawColumn && (
                              <TableCell align="right">
                                {horse.ODRAW}
                              </TableCell>
                            )} */}
                            <TableCell 
                            align='right'
                            >
                              {horse.OWEIGHT}
                            </TableCell>
                            <TableCell align={isFlatRacecard ? 'left' : 'right'}>
                              {isFlatRacecard ? (
                                <Tooltip title={formatNotableText(horse.RacecardNotable)}>
                                  <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }}>
                                    {formatNotableText(horse.RacecardNotable)}
                                  </div>
                                </Tooltip>
                              ) : horse.OALLOW}
                            </TableCell>
                            <TableCell 
                            align='right'
                            >
                              {horse.OR === 'NaN' || horse.OR === 'NA' || horse.OR == null ? '' : horse.OR}
                            </TableCell>
                            <TableCell 
                              align='right'
                              style={smartMasterWaCellStyle}
                            >
                              {formatRacecardDecimal(horse.SmartMasterWgtAdj)}
                            </TableCell>
                            {hasExtendedRacecardAccess?
                              <TableCell 
                                align='right'
                                style={smartMasterTimeCellStyle}
                              >
                                {formatRacecardDecimal(horse.SmartMasterTimePre)}
                              </TableCell> 
                              : null }
                            {hasExtendedRacecardAccess?
                              <TableCell
                                align='right'
                                style={secPotentialCellStyle}
                              >
                                {formatRacecardDecimal(horse.SecPotential)}
                              </TableCell>
                              : null }
                            {hasExtendedRacecardAccess?
                              <TableCell
                                align='right'
                                style={speedPotentialCellStyle}
                              >
                                {formatRacecardDecimal(horse.SpeedPotential)}
                              </TableCell>
                              : null }
                            <TableCell 
                              align='right'
                              style={{ fontWeight: 'bold' }}
                            >
                              {(horse.SmartForm === 'NaN' || horse.SmartForm == null || !horse.SmartFormExists) ? '' : Number(horse.SmartForm).toFixed(1)}
                            </TableCell>
                            <TableCell 
                              align='right'
                              style={{ fontWeight: 'bold' }}
                            >
                              {(horse.SmartTime === 'NaN' || horse.SmartTime == null || !horse.SmartTimeExists) ? '' : Number(horse.SmartTime).toFixed(1)}
                            </TableCell>
                            {hasExtendedRacecardAccess?
                              <TableCell 
                                align='right'
                                style={{ fontWeight: 'bold' }}
                              >
                                {(horse.SmartSecTime === 'NaN' || horse.SmartSecTime == null || !horse.SmartSecTimeExists) ? '' : Number(horse.SmartSecTime).toFixed(1)}
                              </TableCell>
                              : null }
                            <TableCell 
                              align='right'
                              style={{ fontWeight: 'bold' }}
                            >
                              {(horse.SmartRating === 'NaN' || horse.SmartRating == null || !horse.SmartRatingExists) ? '' : Number(horse.SmartRating).toFixed(1)}
                            </TableCell>
                            {role === 'admin' || role === 'friend' || role === 'coolmore' ? (
                              !hiddenColumns.includes('ORpost') ? (
                                <TableCell 
                                  align='right'
                                  style={{ fontWeight: 'bold' }}
                                >
                                  {horse.ORpost === 'NaN' || horse.ORpost == null ? '' : Math.round(Number(horse.ORpost))}
                                </TableCell>
                              ) : null
                            ) : null}
                            
                            {role === 'admin' || role === 'friend' || role === 'blandford' || role === 'coolmore' ? (
                              !hiddenColumns.includes('RPRpost') ? (
                                <TableCell 
                                  align='right'
                                  style={{ fontWeight: 'bold' }}
                                >
                                  {horse.RPRpost === 'NaN' || horse.RPRpost == null ? '' : Math.round(Number(horse.RPRpost))}
                                </TableCell>
                              ) : null
                            ) : null}
                            
                            {role === 'admin' || role === 'friend' || role === 'blandford' || role === 'coolmore' ? (
                              !finalHiddenColumns.includes('TFpost') ? (
                                <TableCell 
                                  align='right'
                                  style={{ fontWeight: 'bold' }}
                                >
                                  {horse.TFpost === 'NaN' || horse.TFpost === 'NA' || horse.TFpost == null || horse.TFpost === 0 || horse.TFpost === '0' || Number(horse.TFpost) === 0 ? '' : Math.round(Number(horse.TFpost))}
                                </TableCell>
                              ) : null
                            ) : null}

                            
                            {role === 'admin' || role === 'friend' || role === 'blandford' || role === 'coolmore' ? (
                              !finalHiddenColumns.includes('TFtfigPost') ? (
                                <TableCell 
                                  align='right'
                                  style={{ fontWeight: 'bold' }}
                                >
                                  {horse.TFtfigPost === 'NaN' || horse.TFtfigPost === 'NA' || horse.TFtfigPost == null || horse.TFtfigPost === 0 || horse.TFtfigPost === '0' || Number(horse.TFtfigPost) === 0 ? '' : Math.round(Number(horse.TFtfigPost))}
                                </TableCell>
                              ) : null
                            ) : null}

                          {/* Third party adder variables - only include for specific roles */}
                            {(role === 'admin' || role === 'friend' || role === 'blandford' || role === 'coolmore') && (
                              <React.Fragment>
                                {!finalHiddenColumns.includes('Source') && (
                                  <TableCell
                                    align='right'
                                    style={{ fontWeight: 'bold' }}
                                  >
                                    {horse.Source !== 'NaN' && horse.Source}
                                  </TableCell>
                                )}
                                
                                {!finalHiddenColumns.includes('RankRnrs') && (
                                  <TableCell
                                    align='right'
                                    style={{ fontWeight: 'bold' }}
                                  >
                                    {horse.RankRnrs === 'NaN' || horse.RankRnrs == null ? '' : Math.round(Number(horse.RankRnrs))}
                                  </TableCell>
                                )}
                                
                                {!finalHiddenColumns.includes('timeRnk') && (
                                  <TableCell
                                    align='right'
                                    style={{ fontWeight: 'bold' }}
                                  >
                                    {horse.timeRnk === 'NaN' || horse.timeRnk == null ? '' : Number(horse.timeRnk).toFixed(1)}
                                  </TableCell>
                                )}
                                
                                {!finalHiddenColumns.includes('secRnk') && (
                                  <TableCell
                                    align='right'
                                    style={{ fontWeight: 'bold' }}
                                  >
                                    {horse.secRnk === 'NaN' || horse.secRnk == null ? '' : Number(horse.secRnk).toFixed(1)}
                                  </TableCell>
                                )}
                                
                                {!finalHiddenColumns.includes('cadenceMax') && (
                                  <TableCell
                                    align='right'
                                    style={{ fontWeight: 'bold' }}
                                  >
                                    {horse.cadenceMax === 'NaN' || horse.cadenceMax == null ? '' : Number(horse.cadenceMax).toFixed(2)}
                                  </TableCell>
                                )}
                                
                                {!finalHiddenColumns.includes('cadenceMaxRnk') && (
                                  <TableCell
                                    align='right'
                                    style={{ fontWeight: 'bold' }}
                                  >
                                    {horse.cadenceMaxRnk === 'NaN' || horse.cadenceMaxRnk == null ? '' : Number(horse.cadenceMaxRnk).toFixed(1)}
                                  </TableCell>
                                )}
                                
                                {!finalHiddenColumns.includes('stdLthMax') && (
                                  <TableCell
                                    align='right'
                                    style={{ fontWeight: 'bold' }}
                                  >
                                    {horse.stdLthMax === 'NaN' || horse.stdLthMax == null ? '' : Number(horse.stdLthMax).toFixed(2)}
                                  </TableCell>
                                )}
                                
                                {!finalHiddenColumns.includes('stdLthRnk') && (
                                  <TableCell
                                    align='right'
                                    style={{ fontWeight: 'bold' }}
                                  >
                                    {horse.stdLthRnk === 'NaN' || horse.stdLthRnk == null ? '' : Number(horse.stdLthRnk).toFixed(1)}
                                  </TableCell>
                                )}
                              </React.Fragment>
                            )}
                              
                          </TableRow>
                        ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </div>
              )
            })}
            </div>
          )}  
  </div>
  )}

export default CountryRacecard
